import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Jumbotron,
  Container,
  Col,
  Row,
  Form,
  Button,
  Modal,
  Spinner,
  ModalBody,
  ModalFooter,
  ButtonGroup,
  NavLink,
} from 'reactstrap';
import { withApollo } from 'react-apollo';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import BackgroundImage from './backgroundImage';
import Texts from './texts';
import TemplateName from './templateName';
import TemplateDatePickers from '../templates-manager/template-components/TemplateDatePickers';
import Previews from './previews';
import { CREATE_TEMPLATE, UPDATE_TEMPLATE } from '../../mutation/mutation';
import './templates.css';
import templatesFonts from '../../fonts/templates-fonts';
import { GET_TEMPLATES, GET_TEMPLATE_CATEGORIES } from '../../query/query';
import storage from '../../providers/storage';
import Tags from '../templates-manager/tags/tags';

export const dataStructureText = {
  fr: null,
  en: null,
  height: 120,
  width: 800,
  x: 0,
  y: 0,
  fontFamily: 'Montserrat-SemiBold',
  fontSize: 100,
  color: '#000000',
  rotate: 0,
  textAlign: 'center',
};

const TemplatesPage = () => {
  const { templateId } = useParams();
  const { duplicate } = useParams();
  const [backgroundImage, setBackgroundImage] = useState(undefined);
  const [awsBackgroundImage, setAwsBackgroundImage] = useState(undefined);
  const [backgroundCoverImage, setBackgroundCoverImage] = useState(undefined);
  const [texts, updateTexts] = useState([]);
  const [usingTemplate, setUsingTemplate] = useState(undefined);
  const [templateName, updateTemplateName] = useState(usingTemplate === undefined ? '' : usingTemplate.name);
  const [modal, toggleModal] = useState(false);
  const [modalSuccess, toggleModalSuccess] = useState(false);
  const [modalError, toggleModalError] = useState(false);
  const [isTextRectShown, setIsTextRectShown] = useState(true);
  const [canSubmit, setCanSubmit] = useState(false);
  const [subCategories, setSubCategories] = useState([]);
  const [subCategoriesList, setSubCategoriesList] = useState([]);

  const {
    data: { savGetTemplates = [] } = {}, loading,
  } = useQuery(
    GET_TEMPLATES, { fetchPolicy: 'cache-and-network' },
  );
  const {
    data: { savGetTemplateCategories: templateCategories = [] } = {},
  } = useQuery(
    GET_TEMPLATE_CATEGORIES,
    { fetchPolicy: 'cache-and-network' },
  );
  const [createTemplate] = useMutation(CREATE_TEMPLATE);
  const [updateTemplate] = useMutation(UPDATE_TEMPLATE);

  const previewsRef = React.useRef();

  useEffect(() => {
    const roles = storage.getRoles() || {};
    const buffUsingTemplate = savGetTemplates.find(({ _id }) => _id === templateId);

    if (buffUsingTemplate && buffUsingTemplate._id && roles.templatesManager) {
      const buffTexts = buffUsingTemplate.elements.map(({
        color, defaultTextEn: en, defaultTextFr: fr, fontFamily,
        fontSize, height, name, rotate, width, x, y,
      }) => ({
        color,
        en,
        fontFamily,
        fontSize,
        fr,
        height,
        name,
        rotate,
        textAlign: 'center',
        width,
        x,
        y,
      }));
      setUsingTemplate(buffUsingTemplate);
      setStatus(buffUsingTemplate.status || 'PENDING');
      updateTemplateName(duplicate ? `${buffUsingTemplate.name} - COPY` : buffUsingTemplate.name);
      setAwsBackgroundImage(buffUsingTemplate.background.url);
      setBackgroundCoverImage(buffUsingTemplate.backgroundCover);
      updateTexts(buffTexts);
      if (previewsRef && previewsRef.current) {
        previewsRef.current.getCanvasImage('fr');
        previewsRef.current.getCanvasImage('en');
      }
    }
  }, [savGetTemplates, templateId, previewsRef]);

  useEffect(() => {
    const roles = storage.getRoles() || {};
    const buffUsingTemplate = savGetTemplates.find(({ _id }) => _id === templateId) || {};
    if (buffUsingTemplate._id && roles.templatesManager) {
      setSubCategories(
        (buffUsingTemplate.subCategories || [])
          .map((subCategoryId) => subCategoriesList.find(({ _id: id }) => subCategoryId === id))
          .filter((subCategory) => !!subCategory),
      );
    }
  }, [savGetTemplates, subCategoriesList, templateId]);

  useEffect(() => {
    if (!templateCategories.length) return;
    setSubCategoriesList(
      [].concat.apply([], [...templateCategories.map(({ subCategories: s }) => s)]),
    );
  }, [templateCategories]);

  useEffect(() => {
    setCanSubmit(!(
      texts.findIndex((x) => !Object.values(x).every((y) => y !== null)) !== -1
      || !backgroundImage
      || !templateName
    ));
  }, [backgroundImage, templateName, texts]);

  const onChangeText = (value, key, index) => {
    const inputTexts = [...texts];
    inputTexts[index][key] = key === 'height' ? value * 4 : value;
    updateTexts(inputTexts);
  };

  const addText = () => {
    const inputTexts = [...texts];
    inputTexts.splice(inputTexts.length, 0, JSON.parse(JSON.stringify(dataStructureText)));
    updateTexts(inputTexts);
  };

  const removeText = (index) => {
    const inputTexts = [...texts];
    inputTexts.splice(index, 1);
    updateTexts(inputTexts);
  };

  const createOrUpdateTemplate = async () => {
    toggleModal(true);
    const thumbnailFr = await previewsRef.current.getCanvasImage('fr');
    const thumbnailEn = await previewsRef.current.getCanvasImage('en');

    const template = {
      background: backgroundImage,
      backgroundCover: backgroundCoverImage,
      name: templateName,
      subCategories: subCategories.map(({ _id: categoriesId }) => categoriesId),
      texts,
      thumbnailFr,
      thumbnailEn,
      status,
      releaseDate: (status === 'PENDING' && releaseDate) ? moment(releaseDate).format('YYYY-MM-DD') : null,
    };

    if (usingTemplate === undefined || duplicate) {
      createTemplate({ variables: { template } })
        .then(() => { toggleModalSuccess(true); })
        .catch((err) => { toggleModalError(err.message); });
    } else {
      updateTemplate({
        variables: {
          templateId: usingTemplate._id,
          updatedTemplate: {
            ...template,
          },
        },
      })
        .then(() => { toggleModalSuccess(true); })
        .catch((err) => { toggleModalError(err.message); });
    }
  };

  const closeModalError = () => {
    toggleModal(false);
    toggleModalError(false);
  };

  const [releaseDate, setReleaseDate] = useState();

  const [status, setStatus] = useState('PENDING');

  return (
    <Jumbotron className="templates">
      {templatesFonts.map(({ fontFamily, name: fontName }) => (
        <div
          key={fontFamily}
          style={{
            fontFamily, position: 'absolute', top: '-100%', opacity: 0,
          }}
        >
          {fontName}
        </div>
      ))}
      <h3 className="display-4">
        {templateId ? 'Modify' : 'Create '}
        Template
      </h3>
      {!templateId && (
        <NavLink href="/templates/createbatch">Create by Batch</NavLink>
      )}
      {(loading && templateId)
        ? (<div style={styles.loading}>Syncing with the server... Please wait. </div>)
        : (
          <Container>
            <Form>
              <TemplateName
                name={templateName}
                updateTemplateName={updateTemplateName}
              />
              <BackgroundImage
                title="Background image"
                awsImage={awsBackgroundImage}
                setImage={setBackgroundImage}
              />
              <BackgroundImage
                title="Background cover image"
                setImage={setBackgroundCoverImage}
              />
              <Row>
                <Col className="col_form">
                  <Texts
                    onChangeText={onChangeText}
                    addText={addText}
                    removeText={removeText}
                    texts={texts}
                    previewsRef={previewsRef}
                  />
                  <Tags
                    subCategories={subCategories}
                    subCategoriesList={subCategoriesList}
                    updateTemplateSubCategories={setSubCategories}
                  />
                  <Row style={{ alignItems: 'center', marginTop: 20 }}>
                    <ButtonGroup style={{ marginLeft: 10 }}>
                      {templateId && (
                      <Button
                        color={status === 'REJECTED' ? 'primary' : 'secondary'}
                        onClick={() => setStatus('REJECTED')}
                      >
                        REJECTED
                      </Button>
                      )}
                      <Button
                        color={status === 'PENDING' ? 'primary' : 'secondary'}
                        onClick={() => setStatus('PENDING')}
                      >
                        PUBLISH LATER
                      </Button>
                      <Button
                        color={status === 'ACCEPTED' ? 'primary' : 'secondary'}
                        onClick={() => setStatus('ACCEPTED')}
                      >
                        PUBLISH NOW
                      </Button>
                      { status === 'PENDING' && (
                        <div>
                          <TemplateDatePickers
                            status="PENDING"
                            releaseDate={releaseDate}
                            setTemplateReleaseDate={setReleaseDate}
                          />
                        </div>
                      )}
                    </ButtonGroup>
                  </Row>
                </Col>
                <Col xs="auto">
                  <Previews
                    backgroundImage={backgroundImage}
                    backgroundCoverImage={backgroundCoverImage}
                    texts={texts}
                    onChangeText={onChangeText}
                    isTextRectShown={isTextRectShown}
                    ref={previewsRef}
                    isUpdatingTemplate={usingTemplate !== undefined}
                  />
                  <Row style={{ alignItems: 'center' }}>
                    <h5 style={{ marginLeft: 30, marginTop: 13 }}>Display text container ?</h5>
                    <ButtonGroup style={{ marginLeft: 10 }}>
                      <Button
                        color={isTextRectShown ? 'primary' : 'secondary'}
                        onClick={() => setIsTextRectShown(true)}
                      >
                        Yes
                      </Button>
                      <Button
                        color={isTextRectShown ? 'secondary' : 'primary'}
                        onClick={() => setIsTextRectShown(false)}
                      >
                        No
                      </Button>
                    </ButtonGroup>
                  </Row>
                </Col>
              </Row>
              <Button
                color="success"
                disabled={!canSubmit}
                onClick={createOrUpdateTemplate}
                style={styles.submitButton}
              >
                {templateId ? 'Save Template' : 'Create Template'}
              </Button>
            </Form>
          </Container>
        )}
      <Modal isOpen={modal} toggle={toggleModal} className="modalTemplate">
        {modalSuccess && (
          <ModalBody>
            Template
            {templateId ? ' Updated' : ' Created'}
            <ModalFooter>
              {/* eslint-disable-next-line no-undef */}
              <Button color="success" onClick={() => window.location.reload()}>Ok</Button>
            </ModalFooter>
          </ModalBody>
        )}
        {modalError && (
          <ModalBody>
            Error creating template :
            {' '}
            {modalError}
            <ModalFooter>
              <Button color="danger" onClick={closeModalError}>Ok</Button>
            </ModalFooter>
          </ModalBody>
        )}
        {!modalSuccess && !modalError && (
          <Spinner color="primary" />
        )}
      </Modal>
    </Jumbotron>
  );
};

const styles = {
  loading: {
    fontSize: 18,
    width: '100%',
    textAlign: 'center',
    marginTop: 30,
  },
  submitButton: {
    marginTop: 20,
  },
};

export default withApollo(TemplatesPage);



// WEBPACK FOOTER //
// ./src/components/templates/templates.js